import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { logoutReason } from '@/composables/useAuth'
import { usePublicToken } from '@/composables/usePublicToken'
import { setDatadogUserAndAccount } from '@/plugins/datadog'
import { clearSurvicateUser } from '@/plugins/survicate'
import { queryClient, idbQueryStorage } from '@/plugins/vue-query'
import useAuthStore from '@/stores/authStore'
import { useDialogsStore } from '@/stores/dialogsStore'

// Keeps the TanStack query cache, the global dialogs and the router in sync with auth
// transitions. App-agnostic core ported from ada-app-ui — token-type classification,
// /user--extended-data handling, creditor onboarding, guard re-validation and return-to are
// intentionally not ported.
export function useAuthEffects() {
  const { clearPublicToken } = usePublicToken()
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()
  const dialogsStore = useDialogsStore()

  // 1) Logout (explicit, idle timeout or refresh failure) → flush cache + redirect
  watch(
    () => authStore.selectedToken == null,
    async (loggedOut) => {
      if (!loggedOut || route.meta.public === true) return
      // Clear analytics identity — RUM/NPS must not keep attributing the
      // logged-out session to the previous user (shared-device privacy).
      setDatadogUserAndAccount(null, null)
      clearSurvicateUser()
      await queryClient.cancelQueries()
      await idbQueryStorage.clear()
      queryClient.clear()

      // The reason stays set until the login view has shown it: a second redirect during the
      // same logout (tab sync) must not land on a bare /authenticate.
      const reason = logoutReason.value
      void router.push(reason != null ? { path: '/authenticate', query: { reason } } : '/authenticate')
    }
  )

  // 1b) Close the global dialogs on EVERY auth transition — both directions. A
  //     login (e.g. synced from another tab) does not pass through the logout
  //     watcher above, but a dialog opened while logged out (pay-with-OCR on the
  //     login screen) must not survive the identity change (shared-device
  //     privacy). Trade-off: an OCR deep-link stash is dropped if the user logs
  //     in instead of paying anonymously — acceptable, the flow itself is intact.
  watch(
    () => authStore.selectedToken != null,
    () => {
      dialogsStore.reset()
      // The public token is a credential minted for the logged-out payer — it must
      // not outlive that identity either (ADA-4432).
      clearPublicToken()
    }
  )

  // 2) Identity gained or switched (token_id change) → navigate to Home, and on a switch also
  //    reset the query cache. Fires in every open tab (useAuthTabSync), which is wanted: a tab
  //    left on another user's case view must not keep showing it. Not `immediate`: on a reload
  //    the guards already bounce a signed-in visitor away from /authenticate.
  watch(
    () => authStore.selectedToken?.payload?.token_id ?? null,
    async (id, oldId) => {
      if (id == null || id === oldId) return

      if (oldId == null) {
        // A fresh tab can receive the peer session (tab sync) before its lazy first route has
        // resolved, so wait for the router to know where it is; already settled on a normal login.
        await router.isReady()
        // Login. A ?token= deep link is stripped from the URL by Authenticate.vue before it logs
        // in, so it lands here as a plain login too. The check stays as a guard for any other
        // path that still carries the token in the query — that view owns what happens next.
        if (route.name === 'Authenticate' && typeof route.query.token !== 'string') void router.push('/')
        return
      }

      // Switch A → B. Watcher 1b only sees null-transitions — an A→B switch keeps the token
      // non-null, but a dialog opened under the previous identity must not survive it
      // (shared-device privacy).
      dialogsStore.reset()
      clearPublicToken()
      await queryClient.cancelQueries()
      await idbQueryStorage.clear()
      void queryClient.resetQueries()
      // Back to Home so the new identity never sees a view built for the old one.
      if (route.name !== 'Home') void router.push('/')
    }
  )
}
